/**
 * ExportButtons component
 * Requirements: 16.8 - Data export functionality (CSV/Excel/PDF/PNG)
 */
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import type { ExportFormat } from '../types';

export interface ExportButtonsProps {
  onExport: (format: ExportFormat) => void;
  isExporting: boolean;
  disabled?: boolean;
}

/**
 * Export button configurations
 */
const exportButtons: {
  format: ExportFormat;
  label: string;
  icon: React.ReactNode;
  tooltip: string;
  color: 'primary' | 'secondary' | 'info' | 'success';
}[] = [
  {
    format: 'csv',
    label: 'CSV',
    icon: <DownloadIcon />,
    tooltip: 'CSVファイルとしてダウンロード',
    color: 'primary',
  },
  {
    format: 'excel',
    label: 'Excel',
    icon: <TableChartIcon />,
    tooltip: 'Excelファイルとしてダウンロード',
    color: 'success',
  },
  {
    format: 'pdf',
    label: 'PDF',
    icon: <PictureAsPdfIcon />,
    tooltip: 'PDFファイルとしてダウンロード',
    color: 'secondary',
  },
  {
    format: 'png',
    label: 'PNG',
    icon: <ImageIcon />,
    tooltip: '画像としてダウンロード',
    color: 'info',
  },
];

export const ExportButtons: React.FC<ExportButtonsProps> = ({
  onExport,
  isExporting,
  disabled = false,
}) => {
  return (
    <Box
      data-testid="export-buttons"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {isExporting && (
        <CircularProgress
          data-testid="export-loading"
          size={24}
          sx={{ mr: 1 }}
        />
      )}

      {exportButtons.map(({ format, label, icon, tooltip, color }) => (
        <Tooltip key={format} title={tooltip}>
          <span>
            <Button
              variant="outlined"
              color={color}
              size="small"
              startIcon={icon}
              onClick={() => onExport(format)}
              disabled={isExporting || disabled}
              aria-label={label}
            >
              {label}
            </Button>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
};
